import { useCallback, useMemo, useState } from 'react';

import type { PersonalisationSlot } from '../../context/personalisation-typedefs';
import { usePersonalisationContext } from '../../context/PersonalisationContext';
import {
  buildGroupedSlotModel,
  getInactiveGroupSlotIds,
  getInputLineValuesForGroup,
  getSlotForInputLine,
  inferActiveGroupName
} from '../../utilities/buildGroupedSlotModel';
import type { PrintSource } from '../../utilities/printCropGeometry';
import type {
  AlphanumericValue,
  GroupedModeSelection,
  OptionGroup
} from '../../utilities/buildGroupedSlotModel';

/** Extract display text from an alphanumeric slot value. */
function getAlphanumericText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'text' in value) {
    const text = (value as AlphanumericValue).text;
    return typeof text === 'string' ? text : '';
  }
  return '';
}

/** Extract font from an alphanumeric slot value. */
function getAlphanumericFont(value: unknown): string | null {
  if (value && typeof value === 'object' && 'font' in value) {
    const font = (value as AlphanumericValue).font;
    return font ?? null;
  }
  return null;
}

/** Parse a slot's allowedFonts, which arrives as either an array or a JSON string. */
function parseAllowedFonts(allowedFonts: unknown): string[] {
  if (!allowedFonts) return [];
  if (Array.isArray(allowedFonts)) return allowedFonts;
  if (typeof allowedFonts === 'string') {
    try {
      return JSON.parse(allowedFonts) as string[];
    } catch {
      return [];
    }
  }
  return [];
}


/** A value that an image or text slot selection can hold at runtime. */
type SlotSelectionValue = string | File | AlphanumericValue | null;

/** One rendered input line within the active option group. */
export interface InputLine {
  inputLineIndex: number;
  slot: PersonalisationSlot;
  inputValue: SlotSelectionValue;
}

/** A choice item for the Choose option selector. */
export interface OptionChoice {
  id: string;
  label: string;
}

/** Image data emitted by PersonalisationImageUploadSlot after a save. */
export interface ImageSavedData {
  selected: File;
  extraImages: Array<{ data: File; type: string }>;
  supportingImage: File | null;
  printSource?: PrintSource | null;
}

export interface GroupedSlotViewModelResult {
  optionGroups: OptionGroup[];
  inputLineCount: number;
  inputLines: InputLine[];
  activeGroupName: string;
  optionSelectionChoices: OptionChoice[];
  shouldShowChooseOption: boolean;
  isImageUploadStep: boolean;
  hasUserProvidedContent: boolean;
  parsedAllowedFontsForGroup: string[];
  hasFontOptionsForGroup: boolean;
  selectedFontForGroup: string;
  fontPreviewLines: string[];
  handleGroupFontChange: (fontName: string) => void;
  handleInputLineChange: (inputLineIndex: number, slotId: string, newValue: unknown) => void;
  handleGroupChange: (newGroupName: string) => void;
  handleImageUploadedInGroup: (inputLineIndex: number, imageData: ImageSavedData) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * View model for GROUPED slot mode.
 *
 * Terminology:
 * - **Option group** — a distinct `groupName` (e.g. Foil, Black, Gold); customer picks one via Choose option.
 * - **Input line** — one customer-facing field within the active option (e.g. UV line 1 / 2 / 3).
 *   Multiple backend slots share a groupName and map to input lines by `orderIndex`.
 */
export function useGroupedSlotViewModel({ slots }: { slots: PersonalisationSlot[] }): GroupedSlotViewModelResult {
  const { selections, updateSelections } = usePersonalisationContext();

  const { optionGroups, inputLineCount } = useMemo(() => buildGroupedSlotModel(slots), [slots]);

  // Cast selections.slots to the broader GroupedModeSelection type since the context's
  // SelectionSlot.selected is typed as `string | null` but holds richer values at runtime.
  const selectionSlots = selections.slots as unknown as GroupedModeSelection[];

  const inferredActiveGroupName = useMemo(
    () => inferActiveGroupName({ optionGroups, selections: selectionSlots }),
    [optionGroups, selectionSlots]
  );

  const [activeGroupNameOverride, setActiveGroupNameOverride] = useState<string | null>(null);

  // The user's explicit font choice for the group. Filled lines also carry the font in
  // their context selections, but this state is the only record of a choice made while
  // all lines are still empty (context selections only exist for filled lines).
  const [explicitGroupFont, setExplicitGroupFont] = useState<string | null>(null);

  const resolvedActiveGroupName = activeGroupNameOverride ?? inferredActiveGroupName;

  const activeOptionGroup = useMemo(
    () => optionGroups.find((group) => group.groupName === resolvedActiveGroupName) ?? optionGroups[0],
    [optionGroups, resolvedActiveGroupName]
  );

  const optionSelectionChoices = useMemo<OptionChoice[]>(
    () => optionGroups.map((group) => ({ id: group.groupName, label: group.groupName })),
    [optionGroups]
  );

  /** One entry per visible input line for the active option group. */
  const inputLines = useMemo<InputLine[]>(() => {
    if (!activeOptionGroup) return [];

    const result: InputLine[] = [];
    for (let inputLineIndex = 0; inputLineIndex < inputLineCount; inputLineIndex++) {
      const slot = getSlotForInputLine(activeOptionGroup, inputLineIndex);
      if (!slot) continue;

      const selection = selectionSlots.find((item) => item.id === slot.id);
      result.push({
        inputLineIndex,
        slot,
        inputValue: (selection?.selected ?? '') as SlotSelectionValue
      });
    }
    return result;
  }, [activeOptionGroup, inputLineCount, selectionSlots]);

  const inputLineHasContent = useCallback((value: unknown): boolean => {
    if (value instanceof File) return true;
    if (typeof value === 'string') return value.trim().length > 0;
    if (value && typeof value === 'object' && 'text' in value) {
      const text = (value as AlphanumericValue).text;
      return Boolean(text && String(text).trim().length > 0);
    }
    return Boolean(value);
  }, []);

  /** True once the customer has typed in any text line or uploaded an image (mirrors font gating). */
  const hasUserProvidedContent = useMemo(
    () => inputLines.some((line) => inputLineHasContent(line.inputValue)),
    [inputLines, inputLineHasContent]
  );

  const shouldShowChooseOption = optionSelectionChoices.length > 1 && hasUserProvidedContent;

  const isImageUploadStep =
    inputLines.length > 0 && inputLines.every((line) => line.slot.slotType === 'IMAGE_UPLOAD');

  // ─── Step-level font state (UV text) ────────────────────────────────────────
  // Font config comes from line 0's slot. The group font is the single source of
  // truth: an explicit choice wins, otherwise it is restored from any filled line's
  // selection (back-navigation), then the admin default, then the first allowed font.

  const line0Slot = inputLines[0]?.slot ?? null;

  // Each group's slots author allowedFonts independently, so the raw order can differ
  // between colour groups. The first group's order is the canonical display order —
  // without it the font cards reshuffle on every colour change (ALBAU-821).
  const canonicalFontOrder = useMemo<string[]>(
    () => parseAllowedFonts(optionGroups[0]?.slots[0]?.allowedFonts),
    [optionGroups]
  );

  const parsedAllowedFontsForGroup = useMemo<string[]>(() => {
    const activeGroupFonts = parseAllowedFonts(line0Slot?.allowedFonts);
    const activeFontSet = new Set(activeGroupFonts);

    const ordered =
      canonicalFontOrder.length === 0
        ? activeGroupFonts
        : [
            ...canonicalFontOrder.filter((font) => activeFontSet.has(font)),
            ...activeGroupFonts.filter((font) => !canonicalFontOrder.includes(font))
          ];

    // The default font always leads the list (and is the initial selection).
    const defaultFont = line0Slot?.defaultFont;
    if (defaultFont && activeFontSet.has(defaultFont)) {
      return [defaultFont, ...ordered.filter((font) => font !== defaultFont)];
    }
    return ordered;
  }, [line0Slot?.allowedFonts, line0Slot?.defaultFont, canonicalFontOrder]);

  const hasFontOptionsForGroup = parsedAllowedFontsForGroup.length > 0;

  const selectedFontForGroup = useMemo<string>(() => {
    if (explicitGroupFont && parsedAllowedFontsForGroup.includes(explicitGroupFont)) {
      return explicitGroupFont;
    }

    // Font may live on any filled line — line 0 is often empty when the user starts on line 2/3.
    for (const { inputValue } of inputLines) {
      const fontFromLine = getAlphanumericFont(inputValue);
      if (fontFromLine && parsedAllowedFontsForGroup.includes(fontFromLine)) {
        return fontFromLine;
      }
    }

    if (line0Slot?.defaultFont && parsedAllowedFontsForGroup.includes(line0Slot.defaultFont)) {
      return line0Slot.defaultFont;
    }
    return parsedAllowedFontsForGroup[0] ?? '';
  }, [explicitGroupFont, inputLines, parsedAllowedFontsForGroup, line0Slot?.defaultFont]);

  /** Preview text for each filled input line, in line order. */
  const fontPreviewLines = useMemo<string[]>(() => {
    return inputLines
      .map(({ inputValue }) => getAlphanumericText(inputValue)?.trim())
      .filter((text) => text.length > 0);
  }, [inputLines]);

  /**
   * Records the group font choice and applies it to every input line in the active
   * group that already has text. Empty lines pick it up when the user types —
   * handleInputLineChange stamps the group font onto every line change.
   */
  const handleGroupFontChange = useCallback(
    (fontName: string) => {
      if (!parsedAllowedFontsForGroup.includes(fontName)) return;

      setExplicitGroupFont(fontName);

      inputLines.forEach(({ slot, inputValue }) => {
        if (slot.slotType !== 'ALPHANUMERIC') return;

        const typedValue = inputValue as AlphanumericValue | string | null;
        const currentText =
          typedValue && typeof typedValue === 'object'
            ? typedValue.text ?? ''
            : typeof typedValue === 'string'
            ? typedValue
            : '';

        if (!currentText.trim()) return;

        const currentTranslatedText =
          typedValue && typeof typedValue === 'object'
            ? (typedValue as AlphanumericValue).translatedText ?? currentText
            : currentText;

        updateSelections({
          slotId: slot.id,
          itemSelected: { text: currentText, translatedText: currentTranslatedText, font: fontName },
          groupLineItemPropertyKey: resolvedActiveGroupName
        });
      });
    },
    [parsedAllowedFontsForGroup, inputLines, resolvedActiveGroupName, updateSelections]
  );

  const handleInputLineChange = useCallback(
    (_inputLineIndex: number, slotId: string, rawNewValue: unknown) => {
      const isAlphanumericObject =
        rawNewValue &&
        typeof rawNewValue === 'object' &&
        !(rawNewValue instanceof File) &&
        'text' in rawNewValue;

      const alphanumericValue = isAlphanumericObject ? (rawNewValue as AlphanumericValue) : null;

      // The group font is the single source of truth — the per-line slot VM keeps its own
      // font state (initialised to the default and never re-synced), so the font it sends
      // with each text change is stale whenever the user has picked a different group font
      // (ALBAU-798). Stamp the group font over whatever came in.
      let processedValue: unknown = rawNewValue;
      if (isAlphanumericObject && selectedFontForGroup) {
        processedValue = { ...alphanumericValue, font: selectedFontForGroup };
      }

      const itemSelected = isAlphanumericObject && alphanumericValue?.text === '' ? null : processedValue;

      updateSelections({ slotId, itemSelected, groupLineItemPropertyKey: resolvedActiveGroupName });
    },
    [resolvedActiveGroupName, selectedFontForGroup, updateSelections]
  );

  /**
   * Called by PersonalisationGroupedSlot when an image is saved in any group slot.
   * Immediately propagates the same image data to every other group at the same input line
   * so users never have to re-upload when switching group options.
   */
  const handleImageUploadedInGroup = useCallback(
    (inputLineIndex: number, imageData: ImageSavedData) => {
      const { selected, extraImages: extraImagesArray, supportingImage, printSource } = imageData;
      if (!selected) return;

      for (const group of optionGroups) {
        if (group.groupName === resolvedActiveGroupName) continue;

        const siblingSlot = getSlotForInputLine(group, inputLineIndex);
        if (!siblingSlot) continue;

        // groupLineItemPropertyKey: null marks this as a propagated staging copy,
        // not an explicit user choice. inferActiveGroupName uses this to determine
        // which group is active without false positives.
        updateSelections({
          slotId: siblingSlot.id,
          itemSelected: selected,
          replaceExtraImages: extraImagesArray ?? [],
          supportingImage: supportingImage ?? null,
          printSource: printSource ?? null,
          groupLineItemPropertyKey: null
        });
      }
    },
    [optionGroups, resolvedActiveGroupName, updateSelections]
  );

  const handleGroupChange = useCallback(
    (newGroupName: string) => {
      if (!newGroupName || newGroupName === resolvedActiveGroupName) return;

      const currentGroup =
        optionGroups.find((group) => group.groupName === resolvedActiveGroupName) ?? activeOptionGroup;

      const inputLineValues = getInputLineValuesForGroup({
        optionGroup: currentGroup,
        selections: selectionSlots,
        inputLineCount
      });

      // Clear only non-image inactive slots. Image slots are propagated immediately on upload
      // (via handleImageUploadedInGroup) so they should never need clearing.
      const allSlotsInGroups = optionGroups.flatMap((g) => g.slots);
      const inactiveSlotIds = getInactiveGroupSlotIds(optionGroups, newGroupName);
      inactiveSlotIds.forEach((slotId) => {
        const slotType = allSlotsInGroups.find((s) => s.id === slotId)?.slotType;
        if (slotType !== 'IMAGE_UPLOAD') {
          updateSelections({ slotId, itemSelected: null });
        }
      });

      setActiveGroupNameOverride(newGroupName);

      const newGroup = optionGroups.find((group) => group.groupName === newGroupName);
      if (!newGroup) return;

      for (let inputLineIndex = 0; inputLineIndex < inputLineCount; inputLineIndex++) {
        const targetSlot = getSlotForInputLine(newGroup, inputLineIndex);
        if (!targetSlot) continue;

        if (targetSlot.slotType === 'IMAGE_UPLOAD') {
          // For image slots: transfer the "active choice" signal by updating
          // groupLineItemPropertyKey. Old group's slot → null, new group's slot → newGroupName.
          // inferActiveGroupName reads groupLineItemPropertyKey for image groups.

          const oldSlot = getSlotForInputLine(currentGroup, inputLineIndex);
          if (oldSlot) {
            const oldSelection = selectionSlots.find((s) => s.id === oldSlot.id) as
              | { selected: unknown; extraImages: unknown; supportingImage: unknown; printSource: unknown }
              | undefined;
            if (oldSelection?.selected) {
              updateSelections({
                slotId: oldSlot.id,
                itemSelected: oldSelection.selected,
                replaceExtraImages: (oldSelection.extraImages as Array<unknown>) ?? [],
                supportingImage: (oldSelection.supportingImage as File | null) ?? null,
                printSource: (oldSelection.printSource as PrintSource | null) ?? null,
                groupLineItemPropertyKey: null
              });
            }
          }

          const newSelection = selectionSlots.find((s) => s.id === targetSlot.id) as
            | { selected: unknown; extraImages: unknown; supportingImage: unknown; printSource: unknown }
            | undefined;
          if (newSelection?.selected) {
            updateSelections({
              slotId: targetSlot.id,
              itemSelected: newSelection.selected,
              replaceExtraImages: (newSelection.extraImages as Array<unknown>) ?? [],
              supportingImage: (newSelection.supportingImage as File | null) ?? null,
              printSource: (newSelection.printSource as PrintSource | null) ?? null,
              groupLineItemPropertyKey: newGroupName
            });
          }
          continue;
        }

        // Text slot — copy value across.
        const lineValue = inputLineValues[inputLineIndex];
        if (lineValue === null || lineValue === undefined || lineValue === '') continue;

        const isEmptyText =
          typeof lineValue === 'object' &&
          lineValue !== null &&
          'text' in lineValue &&
          (!(lineValue as AlphanumericValue).text || ((lineValue as AlphanumericValue).text ?? '').trim() === '');

        if (isEmptyText) continue;

        updateSelections({ slotId: targetSlot.id, itemSelected: lineValue, groupLineItemPropertyKey: newGroupName });
      }
    },
    [activeOptionGroup, optionGroups, resolvedActiveGroupName, inputLineCount, selectionSlots, updateSelections]
  );

  return {
    optionGroups,
    inputLineCount,
    inputLines,
    activeGroupName: resolvedActiveGroupName,
    optionSelectionChoices,
    shouldShowChooseOption,
    isImageUploadStep,
    hasUserProvidedContent,
    parsedAllowedFontsForGroup,
    hasFontOptionsForGroup,
    selectedFontForGroup,
    fontPreviewLines,
    handleGroupFontChange,
    handleInputLineChange,
    handleGroupChange,
    handleImageUploadedInGroup
  };
}
